import React from 'react';
import { OverlayView } from "@react-google-maps/api";
import { ArcButton, ArcCellInfo, ArcIconButton, ArcStack, ArcText } from "@src/core/ui";
import { ReactComponent as CloseIcon } from '@src/assets/icons/close-black.svg';
import { arcSpatium } from "@src/core/extensions";
import { useUnitType } from '@src/core/features/settings';
import { PositionOverlayProps } from "./types";
import { getGeocode } from 'use-places-autocomplete';
import { useCreateOrderMutation } from '@src/features/order/pages/upsert-order-page/redux';
import { useGetHubsQuery } from '@src/features/hubs/redux/api';
import { useGetUserDataQuery } from '@src/core/features';
import { LocationEntity } from '@src/core/entities';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import { routesConfig } from '@src/core/routes';
import { trackError, toTrackedError } from '@src/core/sentry';
import { useFlightProvider } from '@src/features/order/provider';

export const PositionOverlay: React.FC<PositionOverlayProps> = (props) => {
  const {
    position,
    location,
    selectedDrone,
    isOnMission,
    displayCreateMission,
    onClose,
    goTo,
    removePosition,
  } = props;

  const navigate = useNavigate();
  const [createOrder, { isLoading: isSubmitting }] = useCreateOrderMutation();
  const { confirm } = useConfirm();
  const { flightData } = useFlightProvider();
  const { data: hubs = [] } = useGetHubsQuery();
  const { data: userData } = useGetUserDataQuery();
  useUnitType();

  const resolveTakeOffLocation = React.useCallback((): LocationEntity => {
    const droneHub = selectedDrone?.hubId != null
      ? hubs.find((hub) => hub.id === selectedDrone.hubId)
      : undefined;
    const hub = droneHub ?? userData?.hub;
    const hubLat = Number(hub?.latitude);
    const hubLng = Number(hub?.longitude);

    if (hub && Number.isFinite(hubLat) && Number.isFinite(hubLng) && hubLat !== 0 && hubLng !== 0) {
      return {
        latitude: String(hub.latitude),
        longitude: String(hub.longitude),
        address: hub.location ?? hub.name ?? '',
      } as LocationEntity;
    }

    return {
      latitude: selectedDrone?.latitude,
      longitude: selectedDrone?.longitude,
      address: '',
    } as LocationEntity;
  }, [selectedDrone, hubs, userData?.hub]);

  const createMission = React.useCallback(async (position: google.maps.LatLngLiteral) => {
    if(!selectedDrone) return null;
    let address: string;
    try {
      const results = await getGeocode({ location: { lat: position.lat, lng: position.lng } });
      address = results[0].formatted_address;
    } catch (error: unknown) {
      trackError(error instanceof Error ? error : new Error(String(error)), {
        code: 'FE_0008',
        extra: { lat: position.lat, lng: position.lng, source: 'positionOverlayCreateMission' },
      });
      toast.error('Failed to resolve address');
      return;
    }
    try {
      const response = await createOrder({
        takeOffLocation: resolveTakeOffLocation(),
        destinationLocation: {
          latitude: position.lat,
          longitude: position.lng,
          address: address,
        },
        clientName: '',
        packageContent: '',
      }).unwrap();

      const orderId = response.id;
      navigate(`${routesConfig.orders}/create-mission/${orderId}?droneSerial=${selectedDrone.serialNumber}`);
      removePosition?.();
    } catch (error: unknown) {
      trackError(toTrackedError(error), { code: 'FE_0020', extra: { flow: 'positionOverlayCreateOrder' } });
      toast.error('Failed to create order');
      console.error('Create order error:', error);
    }
  }, [selectedDrone, createOrder, navigate, removePosition, resolveTakeOffLocation]);

  const goHere = React.useCallback(async (position: google.maps.LatLngLiteral) => {
    const confirmed = await confirm(
      'The drone will fly to the selected location.',
      'Confirm Go Here?'
    );
    if (!confirmed) return;
    goTo?.(position);
    removePosition?.();
  }, [confirm, goTo, removePosition]);

  const distance = React.useMemo(() => {
    if (!selectedDrone || !position) return null;

    const useTelemetry =
      flightData?.serialNumber === selectedDrone.serialNumber &&
      flightData.latitude != null &&
      flightData.longitude != null;

    const droneLat = useTelemetry
      ? Number(flightData.latitude)
      : Number(selectedDrone.latitude);
    const droneLng = useTelemetry
      ? Number(flightData.longitude)
      : Number(selectedDrone.longitude);

    if (isNaN(droneLat) || isNaN(droneLng)) return null;
    
    const dronePosition = new google.maps.LatLng(droneLat, droneLng);
    const targetPosition = new google.maps.LatLng(position.lat, position.lng);
    
    return google.maps.geometry.spherical.computeDistanceBetween(dronePosition, targetPosition);
  }, [selectedDrone, position, flightData]);

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <ArcStack
        style={{
          position: "relative",
          background: "#fff",
          padding: "12px 16px",
          borderRadius: "8px",
          border: "1px solid rgba(0,0,0,0.2)",
          boxShadow: "0px 2px 7px rgba(0, 0, 0, 0.3)",
          minWidth: location ? "300px" : "250px",
          transform: "translate(-50%, -110%)",
          zIndex: 1000,
        }}
      >
        <ArcIconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </ArcIconButton>
        <ArcStack spacing={2} marginTop={1.5}>
          {location?.address ? (
            <ArcCellInfo label="ADDRESS">
              <ArcText.h5 color="text.night">{location.address}</ArcText.h5>
            </ArcCellInfo>
          ) : null}
          <ArcStack spacing={3} direction="row">
            <ArcCellInfo label="LATITUDE">
              <ArcText.h5 color="text.night">
                {position.lat.toFixed(5)}
              </ArcText.h5>
            </ArcCellInfo>

            <ArcCellInfo label="LONGITUDE">
              <ArcText.h5 color="text.night">
                {position.lng.toFixed(5)}
              </ArcText.h5>
            </ArcCellInfo>
          </ArcStack>
          {selectedDrone && distance !== null && (
            <ArcCellInfo label="DISTANCE FROM DRONE">
              <ArcText.h5 color="text.night">{arcSpatium(distance, { fromSi: true })}</ArcText.h5>
            </ArcCellInfo>
          )}

          <ArcStack spacing={1} direction="row" paddingBottom={1}>
            {isOnMission && (
              <ArcButton.brand fullWidth onClick={() => goHere(position)}>
                Go Here
              </ArcButton.brand>
            )}
            {displayCreateMission && (
              <ArcButton.brand
                loading={isSubmitting}
                fullWidth
                sx={{ fontSize: '12px' }}
                onClick={() => createMission(position)}
              >
                Create Mission
              </ArcButton.brand>
            )}
          </ArcStack>
        </ArcStack>
        <ArcStack
          style={{
            position: "absolute",
            bottom: "-10px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderTop: "10px solid #fff",
          }}
        />
      </ArcStack>
    </OverlayView>
  );
};

